import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getExerciseTemplates } from '../lib/database';
import { useScrollLock } from '../hooks/useScrollLock';
import ExerciseDetailModal from './ExerciseDetailModal';
import './ExerciseCatalogViewer.css';

function ExerciseCatalogViewer({ isOpen, onClose }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);

  useScrollLock(isOpen);

  // Interactive drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const dragStartY = useRef(0);
  const dragSource = useRef(null); // 'handle' or 'content'
  const closeTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  useEffect(() => {
    if (isOpen) {
      loadExercises();
      setDragY(0);
      setIsClosing(false);
    }
  }, [isOpen]);

  const loadExercises = async () => {
    try {
      setLoading(true);
      const data = await getExerciseTemplates();
      setExercises(data);
    } catch (error) {
      console.error('Error loading exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setIsClosing(true);
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsClosing(false);
      setDragY(0);
      onClose();
    }, 250);
  }, [onClose]);

  // --- Interactive drag handlers ---
  const handleTouchStart = useCallback((e) => {
    const content = contentRef.current;
    const touch = e.touches[0];
    const isScrolledToTop = !content || content.scrollTop <= 0;

    // Always allow drag from handle area (top 60px of container)
    const containerRect = containerRef.current?.getBoundingClientRect();
    const touchRelativeY = touch.clientY - (containerRect?.top || 0);
    const isInHandleArea = touchRelativeY < 60;

    if (isInHandleArea) {
      dragSource.current = 'handle';
      dragStartY.current = touch.clientY;
      setIsDragging(true);
      return;
    }

    // From content area, only allow when scrolled to top
    if (isScrolledToTop) {
      dragSource.current = 'content';
      dragStartY.current = touch.clientY;
      // Don't set isDragging yet - wait for move to confirm downward direction
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const delta = touch.clientY - dragStartY.current;

    if (!isDragging && dragSource.current === 'content') {
      // Start drag only if moving downward enough
      if (delta > 8) {
        setIsDragging(true);
        dragStartY.current = touch.clientY; // reset so drag starts from here
        return;
      } else if (delta < -5) {
        // Moving up — cancel, let content scroll
        dragSource.current = null;
        return;
      }
      return;
    }

    if (!isDragging) return;

    // Prevent content scroll while dragging
    if (delta > 0) {
      e.preventDefault();
    }

    // Only allow downward drag (with rubber-band resistance if pulling up)
    const clampedDelta = delta > 0 ? delta : delta * 0.2;
    setDragY(clampedDelta);
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) {
      dragSource.current = null;
      return;
    }

    setIsDragging(false);
    dragSource.current = null;

    // Close if dragged past threshold (120px or 25% of container height)
    const containerHeight = containerRef.current?.offsetHeight || 500;
    const threshold = Math.min(120, containerHeight * 0.25);

    if (dragY > threshold) {
      handleClose();
    } else {
      // Snap back
      setDragY(0);
    }
  }, [isDragging, dragY, handleClose]);

  // Get unique muscle groups for filter
  const muscleGroups = [...new Set(exercises.map(e => e.primary_muscle_group).filter(Boolean))].sort();

  // Filter exercises
  const filteredExercises = exercises.filter(e => {
    const matchesSearch = e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          e.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMuscle = !selectedMuscle || e.primary_muscle_group === selectedMuscle;
    return matchesSearch && matchesMuscle;
  });

  // Group by muscle
  const groupedExercises = filteredExercises.reduce((acc, exercise) => {
    const muscle = exercise.primary_muscle_group || 'Altro';
    if (!acc[muscle]) acc[muscle] = [];
    acc[muscle].push(exercise);
    return acc;
  }, {});

  if (!isOpen) return null;

  // Dynamic styles based on drag
  const backdropOpacity = Math.max(0, 0.5 - (dragY / 600));
  const containerTransform = isClosing
    ? 'translateY(100%)'
    : `translateY(${Math.max(0, dragY)}px)`;
  const containerTransition = isDragging
    ? 'none'
    : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)';

  return (
    <div
      className="catalog-overlay"
      style={{ backgroundColor: `rgba(0,0,0,${backdropOpacity})` }}
      onClick={handleClose}
    >
      <div
        className={`catalog-container${isClosing ? ' catalog-closing' : ''}`}
        ref={containerRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: containerTransform,
          transition: containerTransition,
        }}
      >
        {/* Drag handle */}
        <div className="catalog-handle-area">
          <div className="catalog-handle" />
        </div>

        {/* Header */}
        <div className="catalog-header">
          <h2>Catalogo Esercizi</h2>
          <button className="catalog-close" onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Filters */}
        <div className="catalog-filters">
          <input
            type="text"
            placeholder="Cerca esercizio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="catalog-search"
          />
          <select
            value={selectedMuscle}
            onChange={(e) => setSelectedMuscle(e.target.value)}
            className="catalog-filter-select"
          >
            <option value="">Tutti</option>
            {muscleGroups.map(muscle => (
              <option key={muscle} value={muscle}>{muscle}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div className="catalog-content" ref={contentRef}>
          {loading ? (
            <div className="catalog-loading">
              <div className="catalog-spinner"></div>
              <p>Caricamento...</p>
            </div>
          ) : filteredExercises.length === 0 ? (
            <div className="catalog-empty">
              <p>Nessun esercizio trovato</p>
            </div>
          ) : (
            <div className="catalog-groups">
              {Object.entries(groupedExercises).sort(([a], [b]) => a.localeCompare(b)).map(([muscle, exs]) => (
                <div key={muscle} className="catalog-group">
                  <h3 className="catalog-group-title">{muscle}</h3>
                  <div className="catalog-list">
                    {exs.map(exercise => (
                      <div
                        key={exercise.id}
                        className="catalog-item"
                        onClick={() => setSelectedExercise(exercise)}
                      >
                        <div className="catalog-item-content">
                          <span className="catalog-item-name">{exercise.name}</span>
                          {exercise.description && (
                            <span className="catalog-item-desc">
                              {exercise.description.length > 60
                                ? exercise.description.substring(0, 60) + '...'
                                : exercise.description}
                            </span>
                          )}
                        </div>
                        <div className="catalog-item-indicators">
                          {exercise.media_url && <span className="indicator media">GIF</span>}
                          {exercise.execution_tips && <span className="indicator tips">Tips</span>}
                        </div>
                        <span className="catalog-item-arrow">&rsaquo;</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="catalog-footer">
          <span>{filteredExercises.length} esercizi</span>
        </div>
      </div>

      {/* Exercise Detail Modal — pass exercise prop correctly */}
      {selectedExercise && (
        <ExerciseDetailModal
          exercise={{
            name: selectedExercise.name,
            exercise_template_id: selectedExercise.id,
            notes: selectedExercise.description
          }}
          onClose={() => setSelectedExercise(null)}
        />
      )}
    </div>
  );
}

export default ExerciseCatalogViewer;
